import { ColDef, ValueFormatterParams, ValueGetterParams } from '@ag-grid-community/core';
import { createElement, FunctionComponent, ReactNode } from 'react';

/**
 * Builds a minimal ValueGetterParams-compatible shim for card mode.
 *
 * Existing consumers access: params.data, params.node?.id, params.colDef
 */
function buildValueGetterParams<TData>(colDef: ColDef<TData>, data: TData, rowIndex: number): ValueGetterParams<TData> {
    return {
        data,
        node: { id: String(rowIndex) },
        colDef,
        column: { getColId: () => (colDef as { colId?: string }).colId ?? (colDef.field as string) ?? '' },
        api: null,
        context: undefined,
        getValue: (field: string) => {
            if (data && typeof data === 'object' && field in (data as Record<string, unknown>)) {
                return (data as Record<string, unknown>)[field];
            }
            return undefined;
        },
    } as unknown as ValueGetterParams<TData>;
}

function buildValueFormatterParams<TData>(
    colDef: ColDef<TData>,
    data: TData,
    value: unknown,
    rowIndex: number,
): ValueFormatterParams<TData> {
    return {
        data,
        node: { id: String(rowIndex) },
        colDef,
        column: { getColId: () => (colDef as { colId?: string }).colId ?? (colDef.field as string) ?? '' },
        api: null,
        context: undefined,
        value,
    } as unknown as ValueFormatterParams<TData>;
}

export function resolveFieldValue<TData>(
    colDef: ColDef<TData>,
    data: TData,
    rowIndex: number,
): { rawValue: unknown; displayValue: ReactNode } {
    let rawValue: unknown;

    // 1. valueGetter
    if (typeof colDef.valueGetter === 'function') {
        rawValue = colDef.valueGetter(buildValueGetterParams(colDef, data, rowIndex));
    }
    // 2. field access
    else if (typeof colDef.field === 'string' && data && typeof data === 'object') {
        rawValue = (data as Record<string, unknown>)[colDef.field];
    }

    // Display conversion
    let formattedValue: string | undefined;
    if (typeof colDef.valueFormatter === 'function') {
        const result = colDef.valueFormatter(buildValueFormatterParams(colDef, data, rawValue, rowIndex));
        if (typeof result === 'string') {
            formattedValue = result;
        }
    }

    let displayValue: ReactNode;

    if (typeof colDef.cellRenderer === 'function') {
        const rendererParams = {
            value: rawValue,
            valueFormatted: formattedValue,
            data,
            node: { id: String(rowIndex), data, rowIndex },
            colDef,
            column: { getColId: () => (colDef as { colId?: string }).colId ?? (colDef.field as string) ?? '' },
            api: null,
            context: undefined,
            ...(colDef.cellRendererParams != null && typeof colDef.cellRendererParams === 'object'
                ? (colDef.cellRendererParams as Record<string, unknown>)
                : {}),
        };
        displayValue = createElement(colDef.cellRenderer as FunctionComponent<Record<string, unknown>>, rendererParams);
    } else if (formattedValue !== undefined) {
        displayValue = formattedValue;
    } else if (rawValue === null || rawValue === undefined) {
        displayValue = undefined;
    } else if (typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean') {
        displayValue = String(rawValue);
    } else {
        displayValue = undefined;
    }

    return { rawValue, displayValue };
}
